import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BedDouble,
  Car,
  Package,
  Plane,
  Ship,
  Shield,
  User,
  Users,
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import SectionHeader from '../SectionHeader';
import BookingSection from './BookingSection';
import BookingPriceSidebar from './BookingPriceSidebar';
import BookingLoadingOverlay from './BookingLoadingOverlay';
import BookingTechnicalIssue from './BookingTechnicalIssue';
import {
  BookingBenefitsBar,
  BookingHelpCards,
  BookingProgress,
} from './BookingCheckoutIntro';
import { ContactFields, PassengerFields, UpsellOptions } from './BookingFormFields';
import BookingFareTable from './BookingFareTable';
import { useBookingCheckout } from '../../hooks/useBookingCheckout';
import { useBookingPrice } from '../../hooks/useBookingPrice';
import { SITE_NAME, SITE_TAGLINE } from '../../constants/site';
import {
  CarOfferSummary,
  CruiseOfferSummary,
  FlightOfferSummary,
  HotelOfferSummary,
  PackageOfferSummary,
} from './offerSummaries';

const OFFER_SUMMARY = {
  flight: FlightOfferSummary,
  hotel: HotelOfferSummary,
  car: CarOfferSummary,
  package: PackageOfferSummary,
  cruise: CruiseOfferSummary,
};

const SERVICE_LABELS = {
  flight: 'Flight fare',
  hotel: 'Room rate',
  car: 'Rental rate',
  package: 'Package fare',
  cruise: 'Cruise fare',
};

const STEPS = {
  REVIEW: 1,
  CONTACT: 2,
  TRAVELERS: 3,
  CONFIRM: 4,
};

export default function BookingCheckoutPage({
  title = 'Review Your Booking',
  backLink = '/flight',
  backLabel = 'Back to results',
  serviceType = 'flight',
  payload,
}) {
  const formRef = useRef(null);
  const [step, setStep] = useState(STEPS.REVIEW);
  const { subject, bookingLines, offer, price } = payload;

  const {
    submitContactStep,
    handleFinalSubmit,
    isLoading,
    isContactSending,
    showIssue,
    contactSent,
  } = useBookingCheckout({ subject, bookingLines });

  const OfferSummary = OFFER_SUMMARY[serviceType] || FlightOfferSummary;
  const isFlight = serviceType === 'flight';
  const OfferIcon = {
    flight: Plane,
    hotel: BedDouble,
    car: Car,
    package: Package,
    cruise: Ship,
  }[serviceType] || Plane;
  const baseAmount = price?.base ?? price?.total ?? 0;
  const totalAmount = price?.total ?? baseAmount;
  const travelerLabel = price?.travelerLabel || '1 Traveler(s)';
  const serviceLabel = SERVICE_LABELS[serviceType] || 'Base fare';

  const {
    tierId,
    setTierId,
    webCheckIn,
    setWebCheckIn,
    cancellationProtection,
    setCancellationProtection,
    pricing,
  } = useBookingPrice({
    baseAmount,
    totalAmount,
    travelerLabel,
    serviceLabel,
    isFlight,
  });

  const validateTravelersStep = () => {
    const form = formRef.current;
    if (!form) return false;
    const names = ['title', 'firstName', 'lastName', 'gender', 'dobDay', 'dobMonth', 'dobYear'];
    for (const name of names) {
      const el = form.elements[name];
      if (el && typeof el.reportValidity === 'function' && !el.reportValidity()) {
        return false;
      }
    }
    return true;
  };

  const goNext = async () => {
    if (step === STEPS.REVIEW) {
      setStep(STEPS.CONTACT);
      return;
    }
    if (step === STEPS.CONTACT) {
      const ok = await submitContactStep(formRef.current);
      if (ok) setStep(STEPS.TRAVELERS);
      return;
    }
    if (step === STEPS.TRAVELERS) {
      if (validateTravelersStep()) setStep(STEPS.CONFIRM);
    }
  };

  const goBack = () => {
    if (step > STEPS.REVIEW) setStep((s) => s - 1);
  };

  if (showIssue) {
    return (
      <>
        <Header />
        <section className="page-banner page-banner--compact booking-page-banner">
          <div className="container">
            <SectionHeader light eyebrow={SITE_NAME} title="Booking status" />
          </div>
        </section>
        <div className="booking-page">
          <div className="container booking-page__content booking-page__content--issue">
            <BookingTechnicalIssue />
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const busy = isLoading || isContactSending;

  return (
    <>
      <Header />
      <section className="page-banner page-banner--compact booking-page-banner" aria-label="Checkout header">
        <div className="container">
          <Link to={backLink} className="back-link back-link--light">
            ← {backLabel}
          </Link>
          <div className="booking-page-banner__row">
            <SectionHeader
              light
              eyebrow={`${SITE_NAME} · Secure checkout`}
              title={title}
              subtitle={`${SITE_TAGLINE} — complete each step to submit your booking request.`}
            />
            <BookingProgress active={step} light />
          </div>
        </div>
      </section>

      <div className="booking-page">
        <div className="container booking-page__content">
          <BookingBenefitsBar />

          <form
            ref={formRef}
            className="booking-checkout__form contact-inquiry-form"
            onSubmit={handleFinalSubmit}
            noValidate
          >
            <input type="checkbox" name="botcheck" className="sr-only" tabIndex={-1} autoComplete="off" />
            {isFlight && <input type="hidden" name="ticketTier" value={tierId} />}

            <div className="booking-checkout__grid">
              <div className="booking-checkout__main">
                {step === STEPS.REVIEW && (
                  <div className="booking-step-panel">
                    <BookingSection title="Selected offer" icon={OfferIcon}>
                      <OfferSummary offer={offer} />
                    </BookingSection>
                    <BookingHelpCards />
                  </div>
                )}

                {step === STEPS.CONTACT && (
                  <div className="booking-step-panel">
                    <BookingSection title="Contact details" icon={Users}>
                      <p className="booking-section__intro">
                        Enter your phone and email. We will send your details to our team and continue
                        your booking — no payment is required on this page.
                      </p>
                      <ContactFields />
                      {contactSent && (
                        <p className="booking-step-notice" role="status">
                          Contact details sent. You can update them below if needed before continuing.
                        </p>
                      )}
                    </BookingSection>
                  </div>
                )}

                {step === STEPS.TRAVELERS && (
                  <div className="booking-step-panel">
                    {isFlight && (
                      <>
                        <BookingSection title="Ticket selection" icon={Plane}>
                          <p className="booking-section__intro">
                            Choose the fare that fits your travel plans. Change rules vary by airline.
                          </p>
                          <BookingFareTable
                            selectedTierId={tierId}
                            onTierChange={setTierId}
                            baseFare={baseAmount}
                            travelerCount={pricing.travelers}
                          />
                        </BookingSection>
                        <BookingSection title="Travel add-ons" icon={Shield}>
                          <p className="booking-section__intro">
                            Optional services to make your trip smoother. You can add or skip any item.
                          </p>
                          <UpsellOptions
                            webCheckIn={webCheckIn}
                            onWebCheckInChange={setWebCheckIn}
                            cancellationProtection={cancellationProtection}
                            onCancellationProtectionChange={setCancellationProtection}
                          />
                        </BookingSection>
                      </>
                    )}
                    <BookingSection
                      title={serviceType === 'hotel' ? 'Guest information' : 'Passenger information'}
                      icon={User}
                    >
                      <p className="booking-section__intro">
                        Names must match government-issued ID exactly as they appear on your passport or
                        driver license.
                      </p>
                      <PassengerFields />
                    </BookingSection>
                  </div>
                )}

                {step === STEPS.CONFIRM && (
                  <div className="booking-step-panel">
                    <BookingSection title="Review your trip" icon={OfferIcon}>
                      <OfferSummary offer={offer} />
                    </BookingSection>
                    <BookingSection title="Summary" icon={Users}>
                      <p className="booking-section__intro">
                        A {SITE_NAME} agent will verify availability and contact you to complete payment
                        and issue your confirmation. Click <strong>Confirm booking</strong> to finish.
                      </p>
                      <ul className="booking-review-list">
                        <li>Contact and passenger details will be submitted to our team.</li>
                        <li>No card or payment information is collected on this website.</li>
                        <li>You will be directed to the next screen to complete your request.</li>
                      </ul>
                    </BookingSection>
                  </div>
                )}

                <div className="booking-step-actions">
                  {step > STEPS.REVIEW && (
                    <button
                      type="button"
                      className="btn-secondary booking-step-actions__back"
                      onClick={goBack}
                      disabled={busy}
                    >
                      Back
                    </button>
                  )}
                  {step < STEPS.CONFIRM && (
                    <button
                      type="button"
                      className="btn-gold booking-step-actions__next"
                      onClick={goNext}
                      disabled={busy}
                    >
                      {step === STEPS.CONTACT
                        ? isContactSending
                          ? 'Sending…'
                          : 'Continue & send details'
                        : 'Continue'}
                    </button>
                  )}
                </div>
              </div>

              <BookingPriceSidebar
                travelerLabel={travelerLabel}
                lineItems={pricing.lineItems}
                subtotal={pricing.subtotal}
                taxes={pricing.taxes}
                total={pricing.total}
                selectedTierName={pricing.selectedTierName}
                currency={pricing.currency}
                isFlight={isFlight}
              />

              {step === STEPS.CONFIRM && (
                <div className="booking-confirm-panel">
                  <p className="booking-terms">
                    By clicking <strong>Confirm booking</strong>, you agree to {SITE_NAME}{' '}
                    <Link to="/flight/terms">Terms &amp; Conditions</Link> and{' '}
                    <Link to="/flight/privacy">Privacy Policy</Link>. Fares are subject to availability until
                    ticketed by our team.
                  </p>
                  <button
                    type="submit"
                    className="btn-primary btn-booking-confirm"
                    disabled={isLoading}
                  >
                    Confirm booking
                  </button>
                  <p className="booking-confirm-panel__note">
                    You will continue to the booking status page while our team processes your request.
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {busy && <BookingLoadingOverlay message={isContactSending ? 'Sending your details…' : undefined} />}
      <Footer />
    </>
  );
}
